import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Pagination,
  TextField,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

const DEFAULT_LIMIT = 20;
const PARTICIPANT_LIMIT = 25;

const formatMarks = (value) => {
  const numericValue = Number(value || 0);
  return Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(2);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds || 0);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}m ${secs}s`;
};

const buildImageUrl = (value) => {
  if (!value) {
    return "";
  }

  if (String(value).startsWith("http")) {
    return value;
  }

  return `${Constants.imageDomain}${value}`;
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "AQ";
  }

  return parts
    .slice(0, 2)
    .map((item) => item[0]?.toUpperCase() || "")
    .join("");
};

const participantStatusSx = (status) => {
  const tones = {
    submitted: { color: "#4ade80", backgroundColor: "rgba(34,197,94,0.14)" },
    started: { color: "#38bdf8", backgroundColor: "rgba(56,189,248,0.14)" },
    registered: { color: "#fbbf24", backgroundColor: "rgba(245,158,11,0.14)" },
    cancelled: { color: "#fb7185", backgroundColor: "rgba(244,63,94,0.14)" },
  };
  const tone = tones[status] || tones.registered;

  return {
    ...tone,
    border: `1px solid ${tone.color}66`,
    fontWeight: 700,
    textTransform: "capitalize",
  };
};

const StatPill = ({ label, value, accent }) => (
  <MDBox
    px={2}
    py={1.5}
    borderRadius="xl"
    sx={{
      backgroundColor: accent,
      minWidth: 118,
    }}
  >
    <MDTypography variant="caption" color="white" sx={{ opacity: 0.82 }}>
      {label}
    </MDTypography>
    <MDTypography variant="h6" color="white" fontWeight="bold">
      {value}
    </MDTypography>
  </MDBox>
);

export default function LiveTestLeaderboard() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: DEFAULT_LIMIT,
  });
  const [topper, setTopper] = useState(null);
  const [summary, setSummary] = useState({
    total_participants: 0,
    submitted_participants: 0,
  });
  const [livetest, setLivetest] = useState(null);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("error");
  const [participantLimit, setParticipantLimit] = useState(PARTICIPANT_LIMIT);
  const [participantsDialog, setParticipantsDialog] = useState({
    open: false,
    loading: false,
    docs: [],
    summary: { total_participants: 0, submitted_participants: 0 },
    pagination: {
      page: 1,
      totalPages: 1,
      totalDocs: 0,
      limit: PARTICIPANT_LIMIT,
    },
  });

  const loadParticipants = async (nextPage = 1, nextLimit = participantLimit) => {
    if (init() !== "success") return;

    setParticipantsDialog((current) => ({ ...current, open: true, loading: true }));

    try {
      const response = await axios.get(
        `${Constants.getUrls.getLiveTestLeaderboard}${id}/participants`,
        { params: { page: nextPage, limit: nextLimit } }
      );
      const data = response?.data?.data || {};

      setParticipantsDialog({
        open: true,
        loading: false,
        docs: data.docs || [],
        summary: response?.data?.summary || {
          total_participants: data.totalDocs || 0,
          submitted_participants: 0,
        },
        pagination: {
          page: data.page || nextPage,
          totalPages: data.totalPages || 1,
          totalDocs: data.totalDocs || 0,
          limit: data.limit || nextLimit,
        },
      });
    } catch (error) {
      setParticipantsDialog((current) => ({ ...current, loading: false }));
      setNotification(true);
      setNotiColor("error");
      setMessage(error?.response?.data?.message || "Unable to load participants.");
    }
  };

  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);

      if (init() !== "success") {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${Constants.getUrls.getLiveTestLeaderboard}${id}`, {
          params: {
            page,
            limit,
          },
        });

        setLeaderboard(response?.data?.data?.docs || []);
        setPagination({
          page: response?.data?.data?.page || page,
          totalPages: response?.data?.data?.totalPages || 1,
          totalDocs: response?.data?.data?.totalDocs || 0,
          limit: response?.data?.data?.limit || limit,
        });
        setTopper(response?.data?.topper || null);
        setSummary(
          response?.data?.summary || {
            total_participants: 0,
            submitted_participants: 0,
          }
        );
        setLivetest(response?.data?.livetest || null);
      } catch (error) {
        setNotification(true);
        setNotiColor("error");
        setMessage(error?.response?.data?.message || "Unable to load leaderboard.");
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, [id, page, limit]);

  const topperName = topper?.user?.name || "No topper yet";
  const topperCollege =
    topper?.user?.university?.short_name || topper?.user?.university?.name || "College not available";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ overflow: "hidden" }}>
          <MDBox
            px={3}
            py={3}
            sx={{
              background:
                "linear-gradient(135deg, rgba(14,43,30,1) 0%, rgba(24,73,44,1) 55%, rgba(242,117,0,0.95) 100%)",
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <MDBox>
                <MDTypography variant="button" color="white" sx={{ opacity: 0.78 }}>
                  Live Test Leaderboard
                </MDTypography>
                <MDTypography variant="h4" color="white" fontWeight="bold">
                  {livetest?.title || livetest?.test_name || "Loading leaderboard"}
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                  <MDTypography variant="button" color="white" sx={{ opacity: 0.82 }}>
                    {summary.submitted_participants} submitted out of
                  </MDTypography>
                  <MDButton
                    variant="text"
                    color="white"
                    size="small"
                    onClick={() => loadParticipants(1)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      py: 0,
                      textTransform: "none",
                      textDecoration: "underline",
                    }}
                  >
                    {summary.total_participants} participants — view list
                  </MDButton>
                </MDBox>
              </MDBox>

              <Link to="/live-tests">
                <MDButton variant="contained" color="warning">
                  Back to Live Tests
                </MDButton>
              </Link>
            </MDBox>
          </MDBox>

          <MDBox p={3}>
            {loading ? (
              <Grid container justifyContent="center">
                <MDTypography variant="h6">Loading leaderboard...</MDTypography>
              </Grid>
            ) : (
              <>
                {topper ? (
                  <Card
                    sx={{
                      mb: 3,
                      border: "1px solid rgba(24,53,38,0.08)",
                      boxShadow: "0px 24px 60px rgba(18, 43, 29, 0.08)",
                    }}
                  >
                    <MDBox p={3}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <MDBox display="flex" alignItems="center" gap={2}>
                            {topper?.user?.profile_pic_url ? (
                              <MDBox
                                component="img"
                                src={buildImageUrl(topper.user.profile_pic_url)}
                                alt={topperName}
                                sx={{
                                  width: 92,
                                  height: 92,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "4px solid rgba(242,117,0,0.18)",
                                }}
                              />
                            ) : (
                              <MDBox
                                width="92px"
                                height="92px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="50%"
                                sx={{
                                  background:
                                    "linear-gradient(135deg, rgba(17,68,44,1) 0%, rgba(242,117,0,0.96) 100%)",
                                }}
                              >
                                <MDTypography variant="h3" color="white">
                                  {getInitials(topperName)}
                                </MDTypography>
                              </MDBox>
                            )}

                            <MDBox>
                              <MDTypography variant="button" color="warning" fontWeight="bold">
                                Rank #{topper.rank}
                              </MDTypography>
                              <MDTypography variant="h4" fontWeight="bold">
                                {topperName}
                              </MDTypography>
                              <MDTypography variant="button" color="text">
                                {topperCollege}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <MDBox
                            display="flex"
                            flexWrap="wrap"
                            gap={1.5}
                            justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          >
                            <StatPill
                              label="Marks Obtained"
                              value={formatMarks(topper.marks_obtained)}
                              accent="#183526"
                            />
                            <StatPill label="Correct" value={topper.correct_count} accent="#21633d" />
                            <StatPill
                              label="Incorrect"
                              value={topper.incorrect_count}
                              accent="#8f2d1f"
                            />
                            <StatPill label="Skipped" value={topper.skipped_count} accent="#6b7280" />
                          </MDBox>
                          <MDBox
                            mt={2}
                            display="flex"
                            flexWrap="wrap"
                            gap={2}
                            justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          >
                            <MDTypography variant="button" color="text">
                              Accuracy: {Number(topper.accuracy_percent || 0).toFixed(0)}%
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                              Total Questions: {topper.total_questions || 0}
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                              Time: {formatDuration(topper.total_time_spent_seconds)}
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                              Submitted: {formatDate(topper.submitted_at)}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                ) : null}

                <Grid container spacing={2}>
                  {leaderboard.length ? (
                    leaderboard.map((item) => {
                      const participantName = item?.user?.name || "Unknown User";
                      const participantCollege =
                        item?.user?.university?.short_name ||
                        item?.user?.university?.name ||
                        "College not available";

                      return (
                        <Grid item xs={12} key={item._id}>
                          <Card
                            sx={{
                              border: "1px solid rgba(24,53,38,0.08)",
                              boxShadow: "0px 16px 40px rgba(18, 43, 29, 0.05)",
                            }}
                          >
                            <MDBox p={2.5}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={5}>
                                  <MDBox display="flex" alignItems="center" gap={2}>
                                    <MDBox
                                      minWidth="52px"
                                      height="52px"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      borderRadius="xl"
                                      sx={{
                                        backgroundColor:
                                          item.rank === 1
                                            ? "#f27500"
                                            : item.rank === 2
                                            ? "#2f6a47"
                                            : "#d8e5da",
                                      }}
                                    >
                                      <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                        color={item.rank <= 2 ? "white" : "dark"}
                                      >
                                        #{item.rank}
                                      </MDTypography>
                                    </MDBox>

                                    {item?.user?.profile_pic_url ? (
                                      <MDBox
                                        component="img"
                                        src={buildImageUrl(item.user.profile_pic_url)}
                                        alt={participantName}
                                        sx={{
                                          width: 58,
                                          height: 58,
                                          borderRadius: "50%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <MDBox
                                        width="58px"
                                        height="58px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        borderRadius="50%"
                                        sx={{ backgroundColor: "#143f2d" }}
                                      >
                                        <MDTypography variant="h5" color="white">
                                          {getInitials(participantName)}
                                        </MDTypography>
                                      </MDBox>
                                    )}

                                    <MDBox>
                                      <MDTypography variant="h6" fontWeight="bold">
                                        {participantName}
                                      </MDTypography>
                                      <MDTypography variant="button" color="text">
                                        {participantCollege}
                                      </MDTypography>
                                      <MDTypography variant="caption" display="block" color="text">
                                        Submitted on {formatDate(item.submitted_at)}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                </Grid>

                                <Grid item xs={12} md={7}>
                                  <MDBox
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={1.25}
                                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                  >
                                    <StatPill
                                      label="Marks"
                                      value={formatMarks(item.marks_obtained)}
                                      accent="#183526"
                                    />
                                    <StatPill label="Correct" value={item.correct_count} accent="#21633d" />
                                    <StatPill
                                      label="Incorrect"
                                      value={item.incorrect_count}
                                      accent="#8f2d1f"
                                    />
                                    <StatPill label="Skipped" value={item.skipped_count} accent="#6b7280" />
                                    <StatPill
                                      label="Time Taken"
                                      value={formatDuration(item.total_time_spent_seconds)}
                                      accent="#3d556b"
                                    />
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </MDBox>
                          </Card>
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Card>
                        <MDBox p={3} textAlign="center">
                          <MDTypography variant="h6">No submitted attempts yet.</MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                  )}
                </Grid>

                <MDBox
                  mt={3}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                    <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                      Rows per page
                    </MDTypography>
                    <TextField
                      select
                      size="small"
                      value={limit}
                      onChange={(event) => {
                        setLimit(Number(event.target.value));
                        setPage(1);
                      }}
                      sx={{ width: 82 }}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </TextField>
                    <MDTypography variant="caption" sx={{ color: "#d7e5ff" }}>
                      {pagination.totalDocs
                        ? `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(
                            pagination.page * pagination.limit,
                            pagination.totalDocs
                          )} of ${pagination.totalDocs}`
                        : "0 records"}
                    </MDTypography>
                  </MDBox>
                  {pagination.totalPages > 1 ? (
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.page}
                      onChange={(_, value) => setPage(value)}
                      variant="outlined"
                      shape="rounded"
                      sx={agriPaginationSx}
                    />
                  ) : null}
                </MDBox>
              </>
            )}
          </MDBox>

          <MDSnackbar
            color={notiColor}
            icon="notifications"
            title="AgriQuiz"
            content={message}
            dateTime="Just now"
            open={notification}
            close={() => setNotification(false)}
          />
        </Card>
      </MDBox>

      <Dialog
        open={participantsDialog.open}
        onClose={() =>
          setParticipantsDialog((current) => ({ ...current, open: false }))
        }
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: "#0b1624",
            color: "#fff",
            border: "1px solid rgba(126,170,220,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <MDBox>
            <MDTypography variant="h6" sx={{ color: "#fff" }}>
              Live Test Participants
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
              {participantsDialog.summary.submitted_participants} submitted out of{" "}
              {participantsDialog.summary.total_participants} registered
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() =>
              setParticipantsDialog((current) => ({ ...current, open: false }))
            }
          >
            Close
          </MDButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(126,170,220,0.14)" }}>
          {participantsDialog.loading ? (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                Loading participants...
              </MDTypography>
            </MDBox>
          ) : participantsDialog.docs.length ? (
            <MDBox display="grid" gap={1.25}>
              {participantsDialog.docs.map((participant) => {
                const participantName = participant.user?.name || "-";
                const participantCollege =
                  participant.user?.university?.short_name ||
                  participant.user?.university?.name ||
                  "College not added";

                return (
                  <MDBox
                    key={participant._id}
                    p={1.5}
                    borderRadius="lg"
                    display="flex"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={2}
                    sx={{
                      background: "rgba(17,35,56,0.78)",
                      border: "1px solid rgba(126,170,220,0.12)",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                      {participant.user?.profile_pic_url ? (
                        <MDBox
                          component="img"
                          src={buildImageUrl(participant.user.profile_pic_url)}
                          alt={participantName}
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <MDBox
                          width="46px"
                          minWidth="46px"
                          height="46px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="50%"
                          sx={{ backgroundColor: "#143f2d" }}
                        >
                          <MDTypography variant="button" color="white" fontWeight="bold">
                            {getInitials(participantName)}
                          </MDTypography>
                        </MDBox>
                      )}
                      <MDBox minWidth={0}>
                        {participant.user?._id ? (
                          <Link
                            to={`/user-details/${participant.user._id}`}
                            style={{ textDecoration: "none" }}
                          >
                            <MDTypography variant="button" fontWeight="bold" color="info">
                              {participantName}
                            </MDTypography>
                          </Link>
                        ) : (
                          <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>
                            {participantName}
                          </MDTypography>
                        )}
                        <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                          {participant.user?.mobile || "No mobile"} •{" "}
                          {participant.user?.email || "No email"}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                          {participantCollege} • Registered {formatDate(participant.registered_at)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      gap={1.25}
                      flexWrap="wrap"
                    >
                      {participant.test_submitted === "yes" ? (
                        <>
                          <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                            Rank: {participant.rank || "-"}
                          </MDTypography>
                          <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                            Marks: {formatMarks(participant.marks_obtained)}
                          </MDTypography>
                        </>
                      ) : null}
                      <Chip
                        size="small"
                        label={participant.participant_status || "registered"}
                        sx={participantStatusSx(participant.participant_status)}
                      />
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          ) : (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                No participants registered for this live test.
              </MDTypography>
            </MDBox>
          )}

          {!participantsDialog.loading && participantsDialog.pagination.totalDocs ? (
            <MDBox
              mt={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                  Rows per page
                </MDTypography>
                <TextField
                  select
                  size="small"
                  value={participantLimit}
                  onChange={(event) => {
                    const nextLimit = Number(event.target.value);
                    setParticipantLimit(nextLimit);
                    loadParticipants(1, nextLimit);
                  }}
                  sx={{ width: 82 }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </TextField>
                <MDTypography variant="caption" sx={{ color: "#d7e5ff" }}>
                  {(participantsDialog.pagination.page - 1) *
                    participantsDialog.pagination.limit +
                    1}
                  -
                  {Math.min(
                    participantsDialog.pagination.page * participantsDialog.pagination.limit,
                    participantsDialog.pagination.totalDocs
                  )}{" "}
                  of {participantsDialog.pagination.totalDocs}
                </MDTypography>
              </MDBox>
              {participantsDialog.pagination.totalPages > 1 ? (
                <Pagination
                  page={participantsDialog.pagination.page}
                  count={participantsDialog.pagination.totalPages}
                  variant="outlined"
                  shape="rounded"
                  sx={agriPaginationSx}
                  onChange={(_, nextPage) => loadParticipants(nextPage)}
                />
              ) : null}
            </MDBox>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
